import { useState, useMemo } from 'react';
import { ArrowLeftRight, RotateCcw, FileEdit, ChevronLeft } from 'lucide-react';
import { ElectionDesignation, FINANCE_REFUND_COPY } from '@signalvote/shared';
import type { ContributionDto, ResolutionResult } from '@signalvote/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  useRefundContribution,
  useReattributeContribution,
  useRedesignateContribution,
  refreshContributionReads,
} from '@/hooks/queries/useFinanceContributions';
import { useDonorPickerContacts } from '@/hooks/queries/useDonorPickerContacts';
import { ContactCombobox } from '@/components/finance/shared/classification';
import { useComplianceProfile, useFinanceSettings } from '@/hooks/queries/useFinanceSettings';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { formatAmount } from '../utils';
import { ResolutionChainView } from './ResolutionChainView';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert } from '@/components/ui/alert';
import { CLIENT_CONFLICT_MARKER } from '@/lib/clientConflict';
import { readySettings } from '@/lib/financeSettingsRead';
import { readyProfile, profileUnreadyMessage } from '@/lib/complianceProfileRead';

// ============================================================================
// Types
// ============================================================================

type ResolutionStep = 'options' | 'refund' | 'reattribute' | 'redesignate' | 'advisory';

interface ResolutionOptionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contribution: ContributionDto;
  excessAmount?: number;
  onResolved?: (message: string) => void;
  /**
   * The step to land on when the dialog opens. Defaults to the `'options'`
   * chooser (refund / reattribute / redesignate). A dedicated entry point —
   * e.g. the ledger's row-level "Refund" quick-action — passes `'refund'` so
   * the treasurer skips the chooser and lands straight on the refund form.
   * `resetForm` still returns to `'options'` on close so a reopened dialog from
   * the chooser context behaves normally.
   */
  initialStep?: ResolutionStep;
}

// ============================================================================
// Component
// ============================================================================

export function ResolutionOptionsDialog({
  open,
  onOpenChange,
  contribution,
  excessAmount,
  onResolved,
  initialStep = 'options',
}: ResolutionOptionsDialogProps) {
  const [step, setStep] = useState<ResolutionStep>(initialStep);
  const [error, setError] = useState<string | null>(null);
  // GitHub #687 — the server's federal-gated "re-file this as an amendment" advisory, when the
  // cure landed on an already-filed federal report. Set from the response the treasurer's own
  // click produced, so it is held here rather than derived.
  const [advisory, setAdvisory] = useState<string | null>(null);

  // Issue #249 — a Stripe-Connect (online) donation is refunded in full so the donor's card is
  // actually credited (the gateway can't issue a partial). Lock the amount to the full gift and
  // show a note; the server guard in ResolutionService is the real enforcement.
  const isConnectOrigin = contribution.origin === 'StripeConnect';
  const fullRefundAmount = Math.abs(contribution.amount).toFixed(2);

  // Every cure moves the same figure by default — the computed excess — so the treasurer never
  // does the arithmetic. Blank when the caller hasn't computed one.
  const excessDefault = excessAmount != null ? excessAmount.toFixed(2) : '';

  // Refund state
  const [refundAmount, setRefundAmount] = useState(() =>
    isConnectOrigin ? fullRefundAmount : excessDefault,
  );
  const [refundDate, setRefundDate] = useState(() => getCampaignTodayString());
  const [refundNotes, setRefundNotes] = useState('');

  // Reattribute state. The date is the CURE NOTICE date — when the donor's signed reattribution
  // statement arrived — not the date the money came in. Today is the right default for a statement
  // the treasurer is holding right now; the gift keeps its own date on both halves of the split.
  const [targetContactId, setTargetContactId] = useState('');
  const [reattributeAmount, setReattributeAmount] = useState(() => excessDefault);
  const [reattributeNoticeDate, setReattributeNoticeDate] = useState(() => getCampaignTodayString());

  // Redesignate state. The amount is pre-filled with the computed excess so a treasurer curing an
  // over-limit gift moves only the excess — moving the whole gift to another election just
  // relocates the violation. Blank means the whole contribution.
  const [newDesignation, setNewDesignation] = useState<ElectionDesignation>(ElectionDesignation.General);
  const [noticeDate, setNoticeDate] = useState(() => getCampaignTodayString());
  const [redesignateAmount, setRedesignateAmount] = useState(() => excessDefault);

  // Hooks
  const queryClient = useQueryClient();
  const { mutateAsync: refundAsync, isPending: isRefunding } = useRefundContribution();
  const { mutateAsync: reattributeAsync, isPending: isReattributing } = useReattributeContribution();
  const { mutateAsync: redesignateAsync, isPending: isRedesignating } = useRedesignateContribution();
  const { contacts, prospects, onSearchChange: onContactSearchChange, unready: contactsUnready } = useDonorPickerContacts(targetContactId);
  const settings = readySettings(useFinanceSettings());
  const profileRead = useComplianceProfile();
  const complianceProfile = readyProfile(profileRead);
  const profileUnready = profileUnreadyMessage(profileRead);

  // ADR-078 — offered off the jurisdiction's DECLARED policy, never off which election dates the
  // committee configured. Anything but an explicit `true` (not yet loaded, undeclared, or declared
  // false) offers nothing; refund and reattribution remain, so the treasurer always has a route.
  // The server refuses the same request independently — hiding a control the endpoint still
  // honours would not be a gate.
  const redesignationDeclaredAllowed = complianceProfile?.redesignationAllowed === true;

  const electionOptions = useMemo(() => {
    const opts: { value: ElectionDesignation; label: string }[] = [];
    if (settings?.primaryDate) opts.push({ value: ElectionDesignation.Primary, label: 'Primary' });
    if (settings?.generalDate) opts.push({ value: ElectionDesignation.General, label: 'General' });
    // A runoff redesignation is only offered when the campaign has set a runoff date
    // (AL / GA / Federal can) — mirrors the shared election-designation picker. ADR-076 stores the
    // runoff as a primary-/general-runoff pair (at most one set); coalesce to the single effective one.
    if (settings?.primaryRunoffDate ?? settings?.generalRunoffDate)
      opts.push({ value: ElectionDesignation.Runoff, label: 'Runoff' });
    return opts;
  }, [settings?.primaryDate, settings?.generalDate, settings?.primaryRunoffDate, settings?.generalRunoffDate]);

  const resetForm = () => {
    setStep('options');
    setError(null);
    setAdvisory(null);
    setRefundAmount(isConnectOrigin ? fullRefundAmount : excessDefault);
    setRefundDate(getCampaignTodayString());
    setRefundNotes('');
    setTargetContactId('');
    setReattributeAmount(excessDefault);
    setReattributeNoticeDate(getCampaignTodayString());
    setNewDesignation(ElectionDesignation.General);
    setNoticeDate(getCampaignTodayString());
    setRedesignateAmount(excessDefault);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const handleBack = () => {
    setStep('options');
    setError(null);
  };

  /**
   * The one way a cure ends, shared by all three (GitHub #687). The cure has already
   * succeeded, so the caller hears about it either way; what varies is whether the treasurer
   * still owes the FEC an amendment. When the server says so, hold the dialog open on that
   * advisory — closing would drop the one instruction the correction depends on.
   */
  const finishCure = (result: ResolutionResult, message: string) => {
    onResolved?.(message);
    if (result.advisory) {
      setAdvisory(result.advisory);
      setStep('advisory');
      return;
    }
    handleOpenChange(false);
  };

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleRefund = async () => {
    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0) return;
    setError(null);

    try {
      const result = await refundAsync({
        row: contribution,
        amount,
        date: refundDate,
        notes: refundNotes.trim() || null,
      });
      finishCure(result, `Refund of ${formatAmount(amount)} recorded`);
    } catch (err) {
      if (err instanceof Error && err.message === CLIENT_CONFLICT_MARKER) {
        setError('This contribution was modified by another user. Please refresh and try again.');
        refreshContributionReads(queryClient);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to process refund');
      }
    }
  };

  const handleReattribute = async () => {
    const amount = parseFloat(reattributeAmount);
    if (isNaN(amount) || amount <= 0 || !targetContactId) return;
    setError(null);

    try {
      const result = await reattributeAsync({
        row: contribution,
        targetContactId,
        amount,
        noticeDate: reattributeNoticeDate,
      });
      const targetName = contacts.find((c) => c.id === targetContactId)?.name ?? 'contact';
      finishCure(result, `Reattributed ${formatAmount(amount)} to ${targetName}`);
    } catch (err) {
      if (err instanceof Error && err.message === CLIENT_CONFLICT_MARKER) {
        setError('This contribution was modified by another user. Please refresh and try again.');
        refreshContributionReads(queryClient);
      } else if (err instanceof Error && err.message === 'GateBlocked') {
        const gateResult = (err as any).gateResult;
        setError(gateResult?.message ?? 'Target contact would also exceed limits.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to reattribute');
      }
    }
  };

  // A blank amount is valid — it means the whole contribution. A supplied one must be a positive
  // number no bigger than the gift; the server enforces the same bounds. Say WHY when it doesn't
  // hold: a dead submit button with no explanation leaves the treasurer guessing.
  const fullContributionAmount = Math.abs(contribution.amount);
  const parsedRedesignateAmount = parseFloat(redesignateAmount);
  const redesignateAmountError =
    redesignateAmount.trim() === ''
      ? null
      : isNaN(parsedRedesignateAmount) || parsedRedesignateAmount <= 0
        ? 'Enter an amount greater than zero.'
        : parsedRedesignateAmount > fullContributionAmount
          ? `That's more than the ${formatAmount(fullContributionAmount)} contribution.`
          : null;

  // Splitting part of a gift into the election it is already in moves nothing between elections
  // and just fragments the row. The whole-row move (blank amount) stays available — re-stamping
  // the same election records the donor's written notice.
  const isPartialRedesignation =
    redesignateAmountError === null
    && redesignateAmount.trim() !== ''
    && parsedRedesignateAmount < fullContributionAmount;
  const sameElectionError =
    isPartialRedesignation && newDesignation === contribution.electionDesignation
      ? `This gift is already designated ${contribution.electionDesignation}. Pick the election the excess should move to.`
      : null;

  const isRedesignateValid = !redesignateAmountError && !sameElectionError;

  const handleRedesignate = async () => {
    if (!newDesignation || !noticeDate || !isRedesignateValid) return;
    setError(null);

    // Blank = the whole contribution (the server treats a null amount as today's whole-row move).
    const amount = redesignateAmount.trim() === '' ? null : parseFloat(redesignateAmount);

    try {
      const result = await redesignateAsync({
        row: contribution,
        newDesignation,
        noticeDate,
        amount,
      });
      finishCure(
        result,
        amount == null
          ? 'Redesignation recorded'
          : `Redesignated ${formatAmount(amount)} to ${newDesignation}`,
      );
    } catch (err) {
      if (err instanceof Error && err.message === CLIENT_CONFLICT_MARKER) {
        setError('This contribution was modified by another user. Please refresh and try again.');
        refreshContributionReads(queryClient);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to record redesignation');
      }
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  const parsedRefundAmount = parseFloat(refundAmount);
  const canRefund = !isNaN(parsedRefundAmount) && parsedRefundAmount > 0 && !isRefunding;

  const parsedReattributeAmount = parseFloat(reattributeAmount);
  const canReattribute = !isNaN(parsedReattributeAmount) && parsedReattributeAmount > 0 && !!targetContactId && !!reattributeNoticeDate && !isReattributing;

  const canRedesignate =
    !!newDesignation && !!noticeDate && isRedesignateValid && !isRedesignating;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="resolution-options-dialog">
        <DialogHeader>
          <DialogTitle>
            {step === 'options' && 'Resolve Contribution'}
            {step === 'refund' && 'Process Refund'}
            {step === 'reattribute' && 'Reattribute Contribution'}
            {step === 'redesignate' && 'Record Written Redesignation'}
            {step === 'advisory' && 'One more step'}
          </DialogTitle>
        </DialogHeader>
        {profileUnready && (
          <p role="alert" className="text-sm text-destructive">
            {profileUnready} Whether your state allows redesignation comes from it.
          </p>
        )}

        {/* Back button for sub-steps. Never on the advisory — the cure is already recorded,
            so there is no form behind it to go back to. */}
        {step !== 'options' && step !== 'advisory' && (
          <button
            type="button"
            onClick={handleBack}
            aria-label="Back"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
          >
            <ChevronLeft className="h-4 w-4" /> Back to options
          </button>
        )}

        <ResolutionChainView contributionId={contribution.id} />

        {/* Step: Options */}
        {step === 'options' && (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setStep('refund')}
              className="flex w-full items-center gap-3 rounded-lg border p-3 text-left hover:bg-accent transition-colors"
            >
              <RotateCcw className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Refund Excess</p>
                <p className="text-xs text-muted-foreground">Return excess funds to the donor</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setStep('reattribute')}
              className="flex w-full items-center gap-3 rounded-lg border p-3 text-left hover:bg-accent transition-colors"
            >
              <ArrowLeftRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Reattribute to Family Member</p>
                <p className="text-xs text-muted-foreground">Attribute excess to a spouse or family member</p>
              </div>
            </button>
            {redesignationDeclaredAllowed && (
              <button
                type="button"
                onClick={() => setStep('redesignate')}
                className="flex w-full items-center gap-3 rounded-lg border p-3 text-left hover:bg-accent transition-colors"
              >
                <FileEdit className="h-5 w-5 shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Record Written Redesignation</p>
                  <p className="text-xs text-muted-foreground">Record a written redesignation from the donor</p>
                </div>
              </button>
            )}
          </div>
        )}

        {/* Step: Refund */}
        {step === 'refund' && (
          <div className="space-y-3">
            <div>
              <label htmlFor="refund-amount" className="block text-sm font-medium mb-1">Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <input
                  id="refund-amount"
                  type="text"
                  inputMode="decimal"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  onBlur={() => {
                    const num = parseFloat(refundAmount);
                    if (!isNaN(num)) setRefundAmount(num.toFixed(2));
                  }}
                  disabled={isConnectOrigin}
                  className="w-full rounded-md border pl-7 pr-3 py-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  placeholder="0.00"
                />
              </div>
              {isConnectOrigin && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {FINANCE_REFUND_COPY.OnlineRefundFullOnly}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="refund-date" className="block text-sm font-medium mb-1">Date</label>
              <input
                id="refund-date"
                type="date"
                value={refundDate}
                onChange={(e) => setRefundDate(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="refund-notes" className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                id="refund-notes"
                value={refundNotes}
                onChange={(e) => setRefundNotes(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
                rows={2}
                placeholder="Optional notes"
              />
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => handleOpenChange(false)}
                className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRefund}
                disabled={!canRefund}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRefunding ? 'Processing...' : 'Process Refund'}
              </button>
            </div>
          </div>
        )}

        {/* Step: Reattribute */}
        {step === 'reattribute' && (
          <div className="space-y-3">
            <div>
              <label htmlFor="reattr-contact" className="block text-sm font-medium mb-1">Target Contact *</label>
              <ContactCombobox
                id="reattr-contact"
                value={targetContactId}
                onChange={setTargetContactId}
                contacts={contacts.filter((c) => c.id !== contribution.contactId)}
                unready={contactsUnready}
                prospects={prospects.filter((c) => c.id !== contribution.contactId)}
                onSearchChange={onContactSearchChange}
                placeholder="Select a contact..."
              />
            </div>
            <div>
              <label htmlFor="reattr-amount" className="block text-sm font-medium mb-1">Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <input
                  id="reattr-amount"
                  type="text"
                  inputMode="decimal"
                  value={reattributeAmount}
                  onChange={(e) => setReattributeAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  onBlur={() => {
                    const num = parseFloat(reattributeAmount);
                    if (!isNaN(num)) setReattributeAmount(num.toFixed(2));
                  }}
                  className="w-full rounded-md border pl-7 pr-3 py-2 text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label htmlFor="reattr-notice-date" className="block text-sm font-medium mb-1">
                Signed Reattribution Received *
              </label>
              {/* Bounded exactly as the server bounds it: the statement can't predate the gift it
                  corrects, and it can't have arrived tomorrow. */}
              <input
                id="reattr-notice-date"
                type="date"
                value={reattributeNoticeDate}
                min={contribution.date}
                max={getCampaignTodayString()}
                onChange={(e) => setReattributeNoticeDate(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                When the donor's signed reattribution arrived. The gift keeps the date the money
                came in.
              </p>
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => handleOpenChange(false)}
                className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReattribute}
                disabled={!canReattribute}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isReattributing ? 'Processing...' : 'Reattribute'}
              </button>
            </div>
          </div>
        )}

        {/* Step: Redesignate */}
        {step === 'redesignate' && (
          <div className="space-y-3">
            <div>
              <label htmlFor="redesig-election" className="block text-sm font-medium mb-1">New Election Designation *</label>
              <select
                id="redesig-election"
                value={newDesignation}
                onChange={(e) => setNewDesignation(e.target.value as ElectionDesignation)}
                className="w-full rounded-md border px-3 py-2 text-sm"
              >
                {electionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {sameElectionError && (
                <p className="mt-1 text-xs text-red-700">{sameElectionError}</p>
              )}
            </div>
            <div>
              <label htmlFor="redesig-amount" className="block text-sm font-medium mb-1">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <input
                  id="redesig-amount"
                  type="text"
                  inputMode="decimal"
                  value={redesignateAmount}
                  onChange={(e) => setRedesignateAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  onBlur={() => {
                    const num = parseFloat(redesignateAmount);
                    if (!isNaN(num)) setRedesignateAmount(num.toFixed(2));
                  }}
                  className="w-full rounded-md border pl-7 pr-3 py-2 text-sm"
                  placeholder="0.00"
                />
              </div>
              {redesignateAmountError ? (
                <p className="mt-1 text-xs text-red-700">{redesignateAmountError}</p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  Leave blank to redesignate the entire contribution.
                </p>
              )}
            </div>
            <div>
              <label htmlFor="redesig-notice-date" className="block text-sm font-medium mb-1">Notice Date *</label>
              <input
                id="redesig-notice-date"
                type="date"
                value={noticeDate}
                onChange={(e) => setNoticeDate(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => handleOpenChange(false)}
                className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRedesignate}
                disabled={!canRedesignate}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRedesignating ? 'Processing...' : 'Record Redesignation'}
              </button>
            </div>
          </div>
        )}

        {/* Step: Advisory — the cure is recorded; this is what the treasurer still owes the
            FEC. Alert-don't-block: there is nothing to retry or undo here, only to read. */}
        {step === 'advisory' && (
          <div className="space-y-3">
            <Alert severity="warning">{advisory}</Alert>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => handleOpenChange(false)}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
